import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';
import { CollabSessionErrorEvent } from '@common/sockets/types/collab-session-error-event.type';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { ProjectFileType } from '@portal-core/project-files/enums/project-file-type.enum';
import { ReviewFilesService } from '@portal-core/reviews/review-files/services/review-files.service';
import { CollabFileTextEditorComponent } from '@portal-core/text-editor/components/collab-file-text-editor/collab-file-text-editor.component';
import { NodeViewBase } from '@portal-core/text-editor/util/node-view.base';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { Plugin } from 'prosemirror-state';
import { NodeViewConstructor } from 'prosemirror-view';

@Component({
  selector: 'mc-snippet-node-view',
  templateUrl: './snippet-node-view.component.html',
  styleUrls: ['./snippet-node-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
@AutoUnsubscribe()
export class SnippetNodeViewComponent extends NodeViewBase implements OnInit {
  @ViewChild(CollabFileTextEditorComponent, { static: false }) collabFileTextEditorComponent: CollabFileTextEditorComponent;

  @Input() inline: boolean;
  @Input() nodeViews: Dictionary<NodeViewConstructor>;
  @Input() plugins: Plugin[];
  @Input() schema: CollabSchema;
  @Input() isRecursive: boolean = false;
  @Input() isOuterSnippet: boolean = false;
  @Input() filePath: string;
  @Input() branchName: string;
  @Input() commitId: string;
  @Input() reviewPackageId: number;
  @Input() fileType: ProjectFileType;
  @Input() licenseUser: LicenseUser;
  @Input() projectId: number;
  @Input() projectLanguage: string;

  @HostBinding('class')
  get changeClass(): string {
    return this.inline ? 'mc-node-view-inline-pane' : 'mc-node-view-pane';
  }
  @HostBinding('class.mc-snippet-node-view-inline')
  get inlineClass(): boolean {
    return this.inline;
  }
  @HostBinding('class.mc-snippet-node-view-block')
  get blockClass(): boolean {
    return !this.inline;
  }

  private isCollabSessionLoaded: boolean = false;
  protected fileId: number;
  protected loadingState: LoadingState<string> = new LoadingState<string>();

  public get parentClassNameSuffix(): string {
    return this.inline ? `snippet-node-view-inline${this.isOuterSnippet ? '-outer' : ''}` : 'snippet-node-view-block';
  }

  public get showRemovalMark(): boolean {
    return !this.inline;
  }

  constructor(protected changeDetectorRef: ChangeDetectorRef, protected elementRef: ElementRef, private reviewFilesService: ReviewFilesService, private errorService: ErrorService) {
    super(changeDetectorRef, elementRef);
  }

  ngOnInit() {
    // If it's recursive, we don't need to wait for it to load to display the recursive message
    this.loadingState.update(!this.isRecursive);
    this.fileId = this.reviewFilesService.getItemByProperties('Path', this.filePath, 'ReviewPackageId', this.reviewPackageId)?.Id;
  }

  onCollabSessionLoaded() {
    if (!this.isRecursive && !this.isCollabSessionLoaded) {
      this.loadingState.update(false);
      this.changeDetectorRef.detectChanges();
      this.load.emit();
    }
    this.isCollabSessionLoaded = true;
  }

  onErrorChanged(collabError: CollabSessionErrorEvent) {
    if (collabError == null) {
      this.loadingState.update(this.isRecursive || this.isCollabSessionLoaded ? false : true);
    } else {
      this.loadingState.update(false, 'Unable to load snippet', this.errorService.getErrorMessages(collabError.error));
    }
    this.changeDetectorRef.detectChanges();
  }
}
